// src/components/BlogForm.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Blog create/edit form with:
//   • Rich Title Editor  — select words → assign colors
//   • SEO Section        — slug, meta title, meta description, keywords, OG image
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useCallback, useEffect } from "react";
import { Input, Textarea, Select, FileUpload, ProgressBar, Btn } from "./UI";
import { ColoredText } from "./ColoredText";
import { stripColorTags } from "../utils/parseColoredText";
import { uploadFile } from "../utils/uploadFile";
import { addDocument, updateDocument } from "../hooks/useFirestore";

// ── Color palette ─────────────────────────────────────────────────────────────
const COLOR_OPTIONS = [
  { tag: "yellow", label: "Yellow", hex: "#FFD700" },
  { tag: "white",  label: "White",  hex: "#FFFFFF" },
  { tag: "green",  label: "Green",  hex: "#A8E6A3" },
  { tag: "orange", label: "Orange", hex: "#FF8C00" },
  { tag: "red",    label: "Red",    hex: "#FF6B6B" },
];

const INITIAL_TOPIC = {
  heading: "", content: "",
  imageFile: null, imagePreview: "", imageURL: "",
};

// ── Slug generator ────────────────────────────────────────────────────────────
// Converts any title (including Marathi/Hindi) into a URL-safe slug.
// e.g. "हळदीच्या लागवडीत वाढ, बाजारभावांचा?" → "haldichya-lagvadit-vadh-bajarbhavancha"
// For non-Latin scripts it falls back to a transliteration-friendly lowercase slug.
function generateSlug(text) {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/[\u0900-\u097F]+/g, (match) => {
      // Basic Devanagari → rough romanization for slug
      // In production you can use the `transliterate` npm package for better results
      return match
        .replace(/[अआ]/g, "a").replace(/[इई]/g, "i").replace(/[उऊ]/g, "u")
        .replace(/[एऐ]/g, "e").replace(/[ओऔ]/g, "o")
        .replace(/क/g,"k").replace(/ख/g,"kh").replace(/ग/g,"g").replace(/घ/g,"gh")
        .replace(/च/g,"ch").replace(/छ/g,"chh").replace(/ज/g,"j").replace(/झ/g,"jh")
        .replace(/ट/g,"t").replace(/ड/g,"d").replace(/त/g,"t").replace(/द/g,"d")
        .replace(/न/g,"n").replace(/प/g,"p").replace(/फ/g,"ph").replace(/ब/g,"b")
        .replace(/भ/g,"bh").replace(/म/g,"m").replace(/य/g,"y").replace(/र/g,"r")
        .replace(/ल/g,"l").replace(/व/g,"v").replace(/श/g,"sh").replace(/स/g,"s")
        .replace(/ह/g,"h").replace(/ळ/g,"l").replace(/ड/g,"d").replace(/ञ/g,"n")
        .replace(/[\u093E-\u094F]/g, "").replace(/[\u0902\u0903]/g, "n") // matras & anusvara
        .replace(/[^\w]/g, "");
    })
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

// ── Character counter ─────────────────────────────────────────────────────────
function CharCount({ value, max, warn, danger }) {
  const len = (value || "").length;
  const color = len > danger ? "#e53e3e" : len > warn ? "#d97706" : "#2d7a3a";
  return (
    <span style={{ fontSize: 11, color, fontWeight: 600, marginLeft: 6 }}>
      {len}/{max}
    </span>
  );
}

// ── SEO Preview Card ──────────────────────────────────────────────────────────
function SeoPreview({ metaTitle, metaDesc, slug }) {
  const domain = "agripragati.com";
  const displayUrl = `${domain}/blog/${slug || "your-blog-slug"}`;
  const title = metaTitle || "Your blog title here";
  const desc  = metaDesc  || "Your meta description will appear here in search results...";

  return (
    <div style={{
      border: "1px solid #e0e0e0", borderRadius: 10, padding: "14px 16px",
      background: "#fff", marginTop: 12, fontFamily: "Arial, sans-serif",
    }}>
      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>
        GOOGLE SEARCH PREVIEW
      </div>
      {/* Breadcrumb */}
      <div style={{ fontSize: 12, color: "#202124", marginBottom: 2, display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 14 }}>🌾</span>
        <span style={{ color: "#4d5156" }}>{domain}</span>
        <span style={{ color: "#4d5156" }}>›</span>
        <span style={{ color: "#4d5156" }}>blog</span>
        {slug && <><span style={{ color: "#4d5156" }}>›</span><span style={{ color: "#4d5156" }}>{slug.slice(0, 30)}{slug.length > 30 ? "..." : ""}</span></>}
      </div>
      {/* Title */}
      <div style={{
        fontSize: 18, color: "#1a0dab", marginBottom: 4, lineHeight: 1.3,
        overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
      }}>
        {title.length > 60 ? title.slice(0, 57) + "..." : title}
      </div>
      {/* Description */}
      <div style={{
        fontSize: 13, color: "#4d5156", lineHeight: 1.5,
        overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
      }}>
        {desc.length > 160 ? desc.slice(0, 157) + "..." : desc}
      </div>
    </div>
  );
}

// ── Rich Title Editor ─────────────────────────────────────────────────────────
function RichTitleEditor({ value, onChange, label = "Title", required }) {
  const textareaRef              = useRef(null);
  const [popupVisible, setPopup] = useState(false);
  const [selRange, setSelRange]  = useState(null);

  const handleSelect = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    if (start === end) { setPopup(false); return; }
    setSelRange({ start, end });
    setPopup(true);
  }, []);

  const applyColor = useCallback((colorTag) => {
    if (!selRange) return;
    const { start, end } = selRange;
    const selected = value.slice(start, end);
    const before   = value.slice(0, start);
    const after    = value.slice(end);
    onChange(before + `[${colorTag}]${selected}[/${colorTag}]` + after);
    setPopup(false);
    setSelRange(null);
  }, [selRange, value, onChange]);

  const clearColors = useCallback(() => {
    onChange(stripColorTags(value));
    setPopup(false);
  }, [value, onChange]);

  const plainText = stripColorTags(value);

  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#e53e3e" }}> *</span>}
        <span style={{ fontSize: 11, color: "#999", fontWeight: 400, marginLeft: 8 }}>
          — select any word then click a color
        </span>
      </label>

      <div style={{ position: "relative" }}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => onChange(e.target.value)}
          onMouseUp={handleSelect}
          onKeyUp={handleSelect}
          onBlur={() => setTimeout(() => setPopup(false), 180)}
          rows={3}
          placeholder="Type your title here, then select words and apply colors..."
          style={{
            width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8,
            fontFamily: "inherit", fontSize: 14, resize: "vertical", background: "#fafaf8",
            outline: "none", boxSizing: "border-box",
          }}
        />

        {/* Floating popup */}
        {popupVisible && (
          <div style={{
            position: "absolute", top: -50, left: 0, zIndex: 200,
            background: "#1c1c1c", borderRadius: 10, padding: "7px 12px",
            display: "flex", gap: 7, alignItems: "center",
            boxShadow: "0 6px 24px rgba(0,0,0,0.4)", border: "1px solid #333",
          }}>
            <span style={{ fontSize: 10, color: "#888", marginRight: 2 }}>Color:</span>
            {COLOR_OPTIONS.map(c => (
              <button key={c.tag} onMouseDown={e => { e.preventDefault(); applyColor(c.tag); }} title={c.label}
                style={{
                  width: 24, height: 24, borderRadius: "50%",
                  border: `2px solid ${c.hex === "#FFFFFF" ? "#aaa" : c.hex}`,
                  background: c.hex, cursor: "pointer", transition: "transform 0.12s",
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.3)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
              />
            ))}
            <button onMouseDown={e => { e.preventDefault(); clearColors(); }}
              style={{ background: "#444", border: "none", color: "#ddd", borderRadius: 6,
                fontSize: 10, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit" }}>
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Color toolbar */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 7, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#aaa" }}>Colors:</span>
        {COLOR_OPTIONS.map(c => (
          <button key={c.tag}
            onClick={() => {
              const el = textareaRef.current;
              if (!el) return;
              const s = el.selectionStart, e2 = el.selectionEnd;
              if (s !== e2) { setSelRange({ start: s, end: e2 }); applyColor(c.tag); }
            }}
            style={{
              display: "flex", alignItems: "center", gap: 5, padding: "3px 10px",
              borderRadius: 99, border: `1.5px solid ${c.hex === "#FFFFFF" ? "#ccc" : c.hex}`,
              background: c.hex + "15", color: c.hex === "#FFFFFF" ? "#777" : c.hex,
              fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: c.hex,
              display: "inline-block", border: "1px solid #ccc", flexShrink: 0 }} />
            {c.label}
          </button>
        ))}
        {value !== plainText && (
          <button onClick={clearColors} style={{ padding: "3px 10px", borderRadius: 99,
            border: "1.5px solid #eee", background: "#f9f9f9", color: "#999",
            fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
            ✕ Clear all
          </button>
        )}
      </div>

      {/* Live preview */}
      {plainText && (
        <div style={{ marginTop: 10, borderRadius: 10, overflow: "hidden", border: "1px solid #2a4a2e" }}>
          <div style={{ background: "#2a2a2a", padding: "4px 12px" }}>
            <span style={{ fontSize: 10, color: "#888", fontWeight: 700, letterSpacing: 0.8 }}>
              PREVIEW
            </span>
          </div>
          <div style={{ background: "linear-gradient(135deg, #1a3a1e, #2d5a32)", padding: "14px 20px" }}>
            <ColoredText tagged={value} defaultColor="#FFFFFF"
              style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.5, display: "block" }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── SEO Section ───────────────────────────────────────────────────────────────
function SeoSection({ seo, onChange, plainTitle, thumbnailPreview, thumbnailURL }) {
  const set = (key, val) => onChange({ ...seo, [key]: val });

  // Auto-generate slug from title when slug is empty
  useEffect(() => {
    if (!seo.slug && plainTitle) {
      set("slug", generateSlug(plainTitle));
    }
  }, [plainTitle]); // eslint-disable-line

  // Auto-fill meta title from plain title
  useEffect(() => {
    if (!seo.metaTitle && plainTitle) {
      set("metaTitle", plainTitle.slice(0, 60));
    }
  }, [plainTitle]); // eslint-disable-line

  const handleSlugChange = (val) => {
    // Keep slug clean on every keystroke
    set("slug", val.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-"));
  };

  return (
    <div style={{
      background: "#f6fbf6", border: "1px solid #b2d8b7", borderRadius: 12,
      padding: "18px 20px", marginTop: 8,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 18 }}>🔍</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1a3a1e" }}>SEO Settings</div>
          <div style={{ fontSize: 11, color: "#666" }}>
            These fields help Google find and rank your blog
          </div>
        </div>
      </div>

      {/* URL Slug */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          URL Slug <span style={{ color: "#e53e3e" }}>*</span>
          <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>
            — the URL of your blog post
          </span>
        </label>
        <div style={{ display: "flex", alignItems: "center", border: "1px solid #ddd",
          borderRadius: 8, overflow: "hidden", background: "#fff" }}>
          <span style={{ padding: "9px 10px 9px 12px", background: "#f0f0f0",
            fontSize: 13, color: "#888", borderRight: "1px solid #ddd", whiteSpace: "nowrap" }}>
            agripragati.com/blog/
          </span>
          <input
            value={seo.slug || ""}
            onChange={e => handleSlugChange(e.target.value)}
            placeholder="your-blog-slug"
            style={{ flex: 1, padding: "9px 12px", border: "none", outline: "none",
              fontFamily: "inherit", fontSize: 13, background: "transparent" }}
          />
          <button
            onClick={() => set("slug", generateSlug(plainTitle))}
            title="Re-generate slug from title"
            style={{ padding: "6px 12px", background: "#e8f5e9", border: "none",
              borderLeft: "1px solid #ddd", color: "#2d7a3a", cursor: "pointer",
              fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
            ↻ Auto
          </button>
        </div>
        <p style={{ fontSize: 11, color: "#aaa", margin: "4px 0 0" }}>
          Only lowercase letters, numbers and hyphens. Example: haldichya-lagvad-2025
        </p>
      </div>

      {/* Meta Title */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "flex", alignItems: "center", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          Meta Title
          <CharCount value={seo.metaTitle} max={60} warn={50} danger={60} />
          <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>
            — shown in Google search results
          </span>
        </label>
        <input
          value={seo.metaTitle || ""}
          onChange={e => set("metaTitle", e.target.value)}
          placeholder="Enter meta title (max 60 characters)..."
          maxLength={70}
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd",
            borderRadius: 8, fontFamily: "inherit", fontSize: 14, background: "#fff",
            outline: "none", boxSizing: "border-box",
            borderColor: (seo.metaTitle || "").length > 60 ? "#e53e3e" : "#ddd" }}
        />
      </div>

      {/* Meta Description */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "flex", alignItems: "center", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          Meta Description
          <CharCount value={seo.metaDesc} max={160} warn={140} danger={160} />
          <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>
            — shown below title in Google
          </span>
        </label>
        <textarea
          value={seo.metaDesc || ""}
          onChange={e => set("metaDesc", e.target.value)}
          placeholder="Write a clear 1–2 sentence summary of this blog post (max 160 characters)..."
          maxLength={180}
          rows={3}
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd",
            borderRadius: 8, fontFamily: "inherit", fontSize: 14, background: "#fff",
            outline: "none", resize: "vertical", boxSizing: "border-box",
            borderColor: (seo.metaDesc || "").length > 160 ? "#e53e3e" : "#ddd" }}
        />
      </div>

      {/* Keywords */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          Keywords
          <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>
            — comma separated
          </span>
        </label>
        <input
          value={seo.keywords || ""}
          onChange={e => set("keywords", e.target.value)}
          placeholder="हळद, turmeric farming, market price, शेती, agri news..."
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd",
            borderRadius: 8, fontFamily: "inherit", fontSize: 14, background: "#fff",
            outline: "none", boxSizing: "border-box" }}
        />
        {/* Keyword chips preview */}
        {seo.keywords && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
            {seo.keywords.split(",").map(k => k.trim()).filter(Boolean).map((k, i) => (
              <span key={i} style={{ background: "#e8f5e9", border: "1px solid #b2d8b7",
                borderRadius: 99, padding: "2px 9px", fontSize: 11, color: "#2d7a3a", fontWeight: 600 }}>
                {k}
              </span>
            ))}
          </div>
        )}
        <p style={{ fontSize: 11, color: "#aaa", margin: "4px 0 0" }}>
          5–10 relevant keywords. Include Marathi and English versions for best reach.
        </p>
      </div>

      {/* OG Image */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          OG Image (Social Share Image)
          <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>
            — shown when shared on WhatsApp, Facebook, Twitter
          </span>
        </label>
        {(thumbnailPreview || thumbnailURL) && !seo.ogImageFile ? (
          <div style={{ background: "#e8f5e9", border: "1px solid #b2d8b7", borderRadius: 8,
            padding: "10px 14px", fontSize: 12, color: "#2d7a3a", display: "flex",
            alignItems: "center", gap: 8 }}>
            <span>✅</span>
            <span>Using blog thumbnail as OG image — or upload a custom one below</span>
          </div>
        ) : null}
        <div style={{ marginTop: 8 }}>
          <FileUpload
            label=""
            accept="image/*"
            onFile={f => { set("ogImageFile", f); set("ogImagePreview", URL.createObjectURL(f)); }}
            preview={seo.ogImagePreview || seo.ogImageURL}
            hint="Recommended 1200×630px — shown on social media shares"
          />
        </div>
      </div>

      {/* Canonical URL */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          Canonical URL
          <span style={{ fontSize: 11, color: "#888", fontWeight: 400, marginLeft: 6 }}>
            — optional, leave blank to use default
          </span>
        </label>
        <input
          value={seo.canonical || ""}
          onChange={e => set("canonical", e.target.value)}
          placeholder={`https://agripragati.com/blog/${seo.slug || "your-slug"}`}
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd",
            borderRadius: 8, fontFamily: "inherit", fontSize: 13, background: "#fff",
            outline: "none", boxSizing: "border-box", color: "#555" }}
        />
      </div>

      {/* Index toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
        background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 14px" }}>
        <input
          type="checkbox"
          id="noindex"
          checked={seo.noIndex || false}
          onChange={e => set("noIndex", e.target.checked)}
          style={{ width: 16, height: 16, cursor: "pointer" }}
        />
        <label htmlFor="noindex" style={{ fontSize: 13, color: "#555", cursor: "pointer" }}>
          <strong>No Index</strong> — hide this post from Google
          <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>
            (use for drafts or private posts)
          </span>
        </label>
      </div>

      {/* Google Search Preview */}
      <SeoPreview
        metaTitle={seo.metaTitle}
        metaDesc={seo.metaDesc}
        slug={seo.slug}
      />
    </div>
  );
}

// ── Topic Block ───────────────────────────────────────────────────────────────
const TopicBlock = ({ topic, index, onChange, onRemove, isAdmin }) => (
  <div style={{ background: "#f9fdf9", border: "1px solid #d4ebd6",
    borderRadius: 12, padding: 16, marginBottom: 12 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: "#2d7a3a" }}>📌 Topic {index + 1}</span>
      {isAdmin && (
        <button onClick={() => onRemove(index)} style={{ background: "#fee2e2", border: "none",
          borderRadius: 6, padding: "4px 12px", fontSize: 12, color: "#c53030",
          cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
          ✕ Remove
        </button>
      )}
    </div>
    <Input label="Topic Heading" value={topic.heading}
      onChange={v => onChange(index, "heading", v)} placeholder="Enter topic heading..." />
    <FileUpload label="Topic Image" accept="image/*" onFile={f => onChange(index, "imageFile", f)}
      preview={topic.imagePreview || topic.imageURL}
      hint="Upload image for this topic (JPG, PNG, WebP)" />
    <Textarea label="Topic Content" value={topic.content}
      onChange={v => onChange(index, "content", v)} rows={4}
      placeholder="Write the content for this topic..." />
  </div>
);

// ── Main BlogForm ─────────────────────────────────────────────────────────────
export default function BlogForm({ onSave, onClose, authors = [], categories = [], isAdmin, existing }) {
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState(existing || {
    title: "", subheading: "", authorId: "", date: today,
    category: "", status: "draft",
    topics: [{ ...INITIAL_TOPIC }],
    thumbnailFile: null, thumbnailPreview: "", thumbnailURL: "",
  });

  const [seo, setSeo] = useState(existing?.seo || {
    slug: "", metaTitle: "", metaDesc: "", keywords: "",
    canonical: "", noIndex: false,
    ogImageFile: null, ogImagePreview: "", ogImageURL: "",
  });

  const [progress, setProgress]   = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState("");
  const [activeTab, setActiveTab] = useState("content"); // "content" | "seo"

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const addTopic    = () => set("topics", [...form.topics, { ...INITIAL_TOPIC }]);
  const removeTopic = (i) => { if (form.topics.length > 1) set("topics", form.topics.filter((_, idx) => idx !== i)); };
  const updateTopic = (i, key, val) => {
    const updated = [...form.topics];
    updated[i] = { ...updated[i], [key]: val };
    if (key === "imageFile") updated[i].imagePreview = URL.createObjectURL(val);
    set("topics", updated);
  };

  const plainTitle = stripColorTags(form.title);

  const handleSave = async (status) => {
    if (!plainTitle.trim()) { setError("Title is required."); return; }
    if (!seo.slug.trim())   { setError("URL Slug is required in SEO settings."); setActiveTab("seo"); return; }
    setError(""); setUploading(true);

    try {
      // Upload thumbnail
      let thumbnailURL = form.thumbnailURL || "";
      if (form.thumbnailFile) {
        thumbnailURL = await uploadFile(form.thumbnailFile, "blogs/thumbnails", p => setProgress(5 + p * 0.25));
      }

      // Upload OG image (or use thumbnail)
      let ogImageURL = seo.ogImageURL || thumbnailURL;
      if (seo.ogImageFile) {
        ogImageURL = await uploadFile(seo.ogImageFile, "blogs/og-images", p => setProgress(30 + p * 0.15));
      }

      // Upload topic images
      const processedTopics = await Promise.all(
        form.topics.map(async (topic, i) => {
          let imageURL = topic.imageURL || "";
          if (topic.imageFile) {
            imageURL = await uploadFile(topic.imageFile, "blogs/topics",
              p => setProgress(45 + (i / form.topics.length) * 40));
          }
          return { heading: topic.heading, content: topic.content, imageURL };
        })
      );

      setProgress(90);

      const blogData = {
        // Content fields
        title:           form.title.trim(),
        titlePlain:      plainTitle,
        subheading:      form.subheading.trim(),
        subheadingPlain: stripColorTags(form.subheading),
        authorId:        form.authorId,
        date:            form.date,
        category:        form.category,
        status,
        topics:          processedTopics,
        thumbnailURL,

        // SEO fields — saved as nested object in Firestore
        seo: {
          slug:        seo.slug.trim(),
          metaTitle:   seo.metaTitle.trim()   || plainTitle,
          metaDesc:    seo.metaDesc.trim()     || "",
          keywords:    seo.keywords.trim()     || "",
          canonical:   seo.canonical.trim()    || `https://agripragati.com/blog/${seo.slug}`,
          noIndex:     seo.noIndex             || false,
          ogImageURL,
        },
      };

      if (existing?.id) {
        await updateDocument("blogs", existing.id, blogData);
      } else {
        const id = await addDocument("blogs", blogData);
        blogData.id = id;
      }

      setProgress(100);
      onSave({ ...blogData, id: existing?.id || blogData.id });
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please check your Firebase config and try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: 4 }}>
      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c53030" }}>{error}</div>
      )}
      {uploading && <ProgressBar value={progress} label="Uploading..." />}

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderRadius: 10,
        overflow: "hidden", border: "1px solid #d4ebd6" }}>
        {[
          { id: "content", label: "📝 Content" },
          { id: "seo",     label: "🔍 SEO Settings" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: "10px 0", border: "none", fontFamily: "inherit",
            fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
            background: activeTab === tab.id ? "#2d7a3a" : "#f6fbf6",
            color: activeTab === tab.id ? "#fff" : "#2d7a3a",
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Content Tab ── */}
      {activeTab === "content" && (
        <>
          <RichTitleEditor label="Blog Title" value={form.title}
            onChange={v => set("title", v)} required />
          <RichTitleEditor label="Subheading" value={form.subheading}
            onChange={v => set("subheading", v)} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Select label="Author" value={form.authorId} onChange={v => set("authorId", v)}
              options={authors.map(a => ({ value: a.id, label: a.name }))}
              required placeholder="-- Select Author --" />
            <Input label="Date" type="date" value={form.date}
              onChange={v => set("date", v)} required />
            <div style={{ gridColumn: "1 / -1" }}>
              <Select label="Category" value={form.category}
                onChange={v => set("category", v)} options={categories} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <FileUpload label="Blog Thumbnail" accept="image/*"
                onFile={f => { set("thumbnailFile", f); set("thumbnailPreview", URL.createObjectURL(f)); }}
                preview={form.thumbnailPreview || form.thumbnailURL}
                hint="Recommended 1200×630px — also used as OG image if no custom OG image set" />
            </div>
          </div>

          {/* Topics */}
          <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 800, color: "#1a1a1a" }}>
                Topics <span style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>({form.topics.length})</span>
              </label>
              {isAdmin && (
                <Btn onClick={addTopic} variant="ghost" style={{ fontSize: 12, padding: "5px 14px" }}>
                  + Add Topic
                </Btn>
              )}
            </div>
            {form.topics.map((t, i) => (
              <TopicBlock key={i} topic={t} index={i}
                onChange={updateTopic} onRemove={removeTopic} isAdmin={isAdmin} />
            ))}
          </div>
        </>
      )}

      {/* ── SEO Tab ── */}
      {activeTab === "seo" && (
        <SeoSection
          seo={seo}
          onChange={setSeo}
          plainTitle={plainTitle}
          thumbnailPreview={form.thumbnailPreview}
          thumbnailURL={form.thumbnailURL}
        />
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end",
        paddingTop: 14, borderTop: "1px solid #eee", marginTop: 16, flexWrap: "wrap" }}>
        <Btn onClick={onClose} variant="secondary" disabled={uploading}>Cancel</Btn>
        <Btn onClick={() => handleSave("draft")} variant="ghost" disabled={uploading}>💾 Save Draft</Btn>
        <Btn onClick={() => handleSave("published")} disabled={uploading}>🚀 Publish</Btn>
      </div>
    </div>
  );
}
